import React, { useMemo, useCallback, memo } from 'react';
import { useDispatch } from 'react-redux';
import i18n from 'i18n';
import { map } from 'lodash';
import { DetailsObjectType } from 'store/pages/loaders/loader-types';
import { showOverlay } from 'store/overlay/actions';
import Button from 'components/Button';
import {
  CastList,
  CastTitle,
  CastWrapper,
  DescriptionCastWrapper,
  DescriptionWrapper,
  ExpandTextWrapper,
} from './StyledComponents';

interface IDetailsDescription {
  data: DetailsObjectType;
  maxTextLength: number;
  isVisible: boolean;
  onFocus: () => void;
}

const SHOW_MORE_CONFIG = {
  leaveFor: {
    right: '@COVER',
  },
};

const EXPAND_BUTTON_TEXT_SIZE = 150;
const CUT_REG_EXP = /(\s[А-Яа-яA-Za-z]|[.\s,]){1,2}$/;

const DetailsDescription: React.FC<IDetailsDescription> = ({
  data,
  maxTextLength = 350,
  isVisible,
  onFocus,
}) => {
  const dispatch = useDispatch();
  const description = (data.description || '').replace(/<a[^>]*>(.*?)<\/a>/, '$1');

  const normalizedDescription = useMemo(() => {
    if (description.length <= maxTextLength) return description;
    return `${description
      .slice(0, maxTextLength - EXPAND_BUTTON_TEXT_SIZE - 3)
      // replace last 2-3 symbols and replace them with 3 dot
      .replace(CUT_REG_EXP, '')}...`;
  }, [description, maxTextLength]);

  const isExpandTextButtonVisible = normalizedDescription !== description;

  const actorsInfo = map(data.persons, ({ first_name, last_name }) => `${first_name} ${last_name}`)
    .slice(0, 10)
    .join(', ');

  const onShowMore = useCallback(() => {
    dispatch(
      showOverlay({
        type: 'text',
        returnFocusTo: '@EXPAND_DESCRIPTION_BUTTON',
        options: { fullDescription: description },
      }),
    );
  }, [description, dispatch]);

  return (
    <DescriptionCastWrapper isVisible={isVisible}>
      <DescriptionWrapper dangerouslySetInnerHTML={{ __html: normalizedDescription }} />
      {isExpandTextButtonVisible && (
        <ExpandTextWrapper
          id="expland-section"
          sectionName={'EXPAND_DESCRIPTION_BUTTON'}
          config={SHOW_MORE_CONFIG}
        >
          <Button id="more-button" onFocus={onFocus} onEnter={onShowMore}>
            {i18n.t('detailsVOD.moreButton')}
          </Button>
        </ExpandTextWrapper>
      )}
      {actorsInfo && (
        <CastWrapper>
          <CastTitle>{i18n.t('detailsVOD.actors')}</CastTitle>
          <CastList>{actorsInfo}</CastList>
        </CastWrapper>
      )}
    </DescriptionCastWrapper>
  );
};

export default memo(DetailsDescription);
